var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { isAfter } from "date-fns";
import { checkUserType } from "healthex-common/checkusertype";
import { ProjectKey, ProjectPageName, ProjectSectionName, ProjectStaticPageName, } from "healthex-common/project.data.dtos";
import { getProjectData, getProjectDataForStudy, getProjectKey, getProjectKeyFromStudyOrNull, updateProjectDataForStudy, } from "healthex-common/project.data.utils";
import { ScreenshotMetadataStatus } from "healthex-common/screenshot.dtos";
import { StudyAccessData, StudyImportMethod, StudyInvestigationType, StudySectionImageStatus, StudySectionType, StudyStatus, StudyTranslationImportType, StudyTranslationLanguage, StudyType, StudyVersionStatus, } from "healthex-common/study.dtos";
import { UserType } from "healthex-common/user.dtos";
import { asEnum, deepCopy, filterNull, isEnum, isKeyOfObject, setDiff, slugify, uniqueArray, unslugify, unslugifyOptional, } from "healthex-common/utils";
import { computed, observable, reaction, when } from "mobx";
import React from "react";
import { scroller } from "react-scroll";
import { NOT_LOADED } from "../../Infra/RemoteData";
import { AIGeneratedFieldsModel } from "../AddProjectJoinCommon/AIGeneratedFieldsModel";
import { FormModel } from "../FormModel";
import { NamedRoutes } from "../NamedRoutes";
import { getTranslatableFieldsFromProjectSections, getTranslatableFieldsFromStudy, getTranslatableSectionsFromProjectSections, translationInputReference, } from "../ProjectTranslationService";
import { findMatchingNamedRouteParent, routeEquals } from "../RoutingUIManager";
import { SimpleDialogModel } from "../SimpleDialogModel";
import { AddProjectFormUIManager } from "./AddProjectFormUIManager";
import { headerAndFooterHeightRaw } from "./AddProjectMainView.styles";
import { addProjectPagesParentPaddingBottom } from "./AddProjectPagesView.styles";
import { AddStudyPostActivateDialogContent } from "./AddStudyPostActivateDialog";
import { navSectionExtraOffset, navSectionId, navSectionsContainerId } from "./SidePanel/AddProjectSidePanel.styles";
export const pulledFromStudyDetailsText = "This information was pulled from study details. Edits here won't affect the study details, and future updates to Study Details won't apply here.";
export const AllProjectPageNames = { ...ProjectPageName, ...ProjectStaticPageName };
export var PreviewPDFStatusUI;
(function (PreviewPDFStatusUI) {
    PreviewPDFStatusUI["DOWNLOAD"] = "DOWNLOAD";
    PreviewPDFStatusUI["GENERATE"] = "GENERATE";
    PreviewPDFStatusUI["GENERATING"] = "GENERATING";
    PreviewPDFStatusUI["FAILED"] = "FAILED";
})(PreviewPDFStatusUI || (PreviewPDFStatusUI = {}));
export class MajorChangesDialogModel extends SimpleDialogModel {
    constructor() {
        super(...arguments);
        this.changesType = null;
    }
}
__decorate([
    observable
], MajorChangesDialogModel.prototype, "changesType", void 0);
export class AddProjectUIManager extends AddProjectFormUIManager {
    constructor(studyManager, studyService, routingUIManager, authManager, organizationsManager, dialogUIManager, provisioningService, protocolIngestionService, studySectionImageService, bannerUIManager, persistence) {
        super(studyManager, studyService, routingUIManager, authManager, provisioningService);
        this.organizationsManager = organizationsManager;
        this.dialogUIManager = dialogUIManager;
        this.protocolIngestionService = protocolIngestionService;
        this.studySectionImageService = studySectionImageService;
        this.bannerUIManager = bannerUIManager;
        this.persistence = persistence;
        this.isAddingAndConfirmingStudy = false;
        this.shouldShowPatientExperienceCompletedDialog = true;
        this.translationsManuallyCompleted = true;
        this.selectedTranslationFilter = "ALL_SECTIONS";
        this.translationsAddedThisSession = false;
        this.didDownloadTranslationsThisSession = false;
        this.outOfDateImportedTranslations = {};
        this.confirmAndAddStudyForm = new FormModel({
            confirmed: false,
            unreviewedContentConfirmed: false,
        }, []);
        this.currentStudyHash = null;
        this.currentTab = "DOCUMENT";
        this.informedConsentLockedDialogModel = new SimpleDialogModel();
        this.hasPageBeenVisited = false;
        this.hasSectionBeenVisited = false;
        this.currentPageLoading = false;
        this.topBannerHeight = 0;
        this.lastSectionOffset = null;
        this.didCloseTranslationsRequirementsChangedBanner = false;
        this.currentProjectSectionName = null;
        this.isScrollingToPageSection = false;
        this.majorChangesDialogModel = new MajorChangesDialogModel();
        this.selectedLanguageTranslationsCompletedBeforeUpdate = false;
        this.patientExperienceCompletedBeforeUpdate = false;
        this.aiGeneratedFieldsModel = new AIGeneratedFieldsModel(studyManager, (update) => this.studyFormModel.updateInput(update));
        reaction(() => routingUIManager.currentRoute, async () => {
            this.cleanupDialogs();
            this.redirectToStudyManagerIfNecessary();
        }, { fireImmediately: true, equals: routeEquals });
        reaction(() => this.studyExistsButHasNoDraft, async () => {
            this.redirectToStudyManagerIfNecessary();
        }, { fireImmediately: true });
        reaction(() => this.currentPage, async () => {
            this.currentPageLoading = true;
            try {
                await this.refreshStudy({ preserveDirty: true });
            }
            finally {
                this.currentPageLoading = false;
            }
        }, { fireImmediately: true });
        reaction(() => this.currentProjectPageNameRoute, async () => {
            await when(() => this.study !== NOT_LOADED);
            await this.updateVisitedPageIfNecessary();
        }, { fireImmediately: true });
        reaction(() => this.currentProjectSectionName, async () => {
            await when(() => this.study !== NOT_LOADED);
            await this.updateVisitedSectionIfNecessary();
        }, { fireImmediately: true });
        reaction(() => this.studyId, async () => {
            this.cleanupConfirmAndAddStudyLocalForm();
            this.enrollmentVideoUploadingInfo = null;
            this.hasPageBeenVisited = false;
            this.hasSectionBeenVisited = false;
            this.shouldShowPatientExperienceCompletedDialog = false;
            this.translationsAddedThisSession = false;
            this.didDownloadTranslationsThisSession = false;
            this.translationsManuallyCompleted = false;
            this.selectedTranslationFilter = "ALL_SECTIONS";
            this.didCloseTranslationsRequirementsChangedBanner = false;
            this.outOfDateImportedTranslations = {};
            this.studyFormModel.reset();
            this.patientExperienceCompletedBeforeUpdate = false;
        }, { fireImmediately: true });
        reaction(() => [this.currentPage, this.informedConsentSectionLockedByOtherUser], async () => {
            await this.showInformedConsentLockedIfAppropriate();
        }, { fireImmediately: true });
        reaction(() => this.shouldInformedConsentBeLockedByUs, async () => {
            await this.updateInformedConsentLocked(() => this.shouldInformedConsentBeLockedByUs);
        });
        reaction(() => this.translationsCompletedLookup, async () => {
            var _a, _b;
            if (this.study === NOT_LOADED || this.study === null)
                return;
            const studyId = this.studyId;
            const orgId = this.authManager.orgId;
            if (!studyId || !orgId)
                return;
            const completed = this.translationsCompletedLookup[this.selectedTranslationLanguage];
            const studyCompleted = (_b = (_a = this.study.studyTranslatableFields) === null || _a === void 0 ? void 0 : _a.find((t) => t.language === this.selectedTranslationLanguage)) === null || _b === void 0 ? void 0 : _b.completed;
            if (completed !== studyCompleted) {
                await this.studyService.updateStudyVersion(studyId, orgId, {
                    studyTranslatableFields: [{ language: this.selectedTranslationLanguage, completed }],
                });
            }
        });
        routingUIManager.registerRedirect(NamedRoutes.PROJECTS_ADD_PROJECT, (routeParams) => {
            var _a;
            const page = (_a = unslugifyOptional(routeParams.page)) !== null && _a !== void 0 ? _a : "";
            const projectId = routeParams === null || routeParams === void 0 ? void 0 : routeParams.projectId;
            if (projectId && !(isEnum(ProjectPageName)(page) || isEnum(ProjectStaticPageName)(page))) {
                return {
                    route: NamedRoutes.PROJECTS_ADD_PROJECT,
                    routeParams: { projectId, page: slugify(ProjectPageName.PROJECT_DETAILS) },
                };
            }
            else {
                return null;
            }
        });
    }
    get isAddProjectFlow() {
        return this.projectKey === ProjectKey.GENERALDATAUSE_IDENTIFIED_PDDE;
    }
    get isResearchProject() {
        if (this.projectKey === null)
            return false;
        return [
            ProjectKey.RESEARCH_OTHERORREGISTRY_DEIDENTIFIED_NONPDDE,
            ProjectKey.RESEARCH_OTHERORREGISTRY_IDENTIFIED_NONPDDE,
            ProjectKey.RESEARCH_INTERVENTIONAL_IDENTIFIED_NONPDDE,
            ProjectKey.RESEARCH_OTHERORREGISTRY_IDENTIFIED_PDDE,
        ].includes(this.projectKey);
    }
    get projectKeyParams() {
        if (this.study === NOT_LOADED || this.study === null)
            return null;
        const { projectType, studyType, accessData, pdda } = this.study;
        return { projectType, studyType, accessData, pdda };
    }
    get projectKey() {
        if (this.projectKeyParams === null)
            return null;
        const { projectType, studyType, accessData, pdda } = this.projectKeyParams;
        try {
            return getProjectKey(projectType, studyType, accessData, pdda);
        }
        catch (e) {
            return null;
        }
    }
    get projectData() {
        if (this.projectKey === null)
            return null;
        return getProjectData(this.projectKey);
    }
    get projectDataPages() {
        if (this.projectData === null)
            return null;
        return getProjectDataForStudy(this.projectData);
    }
    get projectPages() {
        if (this.projectDataPages === null || this.study === NOT_LOADED || this.study === null || this.currentPage === null)
            return null;
        const data = JSON.parse(JSON.stringify(this.projectDataPages));
        return updateProjectDataForStudy(data, this.study, this.currentPage, this.studyFormModel.input.additionalSections);
    }
    getOverallProgressForStudy(study) {
        var _a, _b;
        if (!study)
            return null;
        const projectKey = getProjectKeyFromStudyOrNull(study);
        const projectData = getProjectData(projectKey);
        const projectPages = updateProjectDataForStudy(getProjectDataForStudy(projectData), study, null, (_b = (_a = study.additionalSections) === null || _a === void 0 ? void 0 : _a.filter((section) => section.language === StudyTranslationLanguage.en)) !== null && _b !== void 0 ? _b : []);
        return this.getOverallProgress(projectPages, this.getIsPatientExperienceCompleted(projectPages), this.getRawAreTranslationsInProgress(study, projectPages));
    }
    getOverallProgress(projectPages, isPatientExperienceCompleted, areTranslationsInProgress) {
        var _a;
        let progress = (_a = projectPages === null || projectPages === void 0 ? void 0 : projectPages.progress) !== null && _a !== void 0 ? _a : 0;
        if (isPatientExperienceCompleted && areTranslationsInProgress) {
            progress *= 0.7;
        }
        return progress;
    }
    get overallProgress() {
        return this.getOverallProgress(this.projectPages, this.isPatientExperienceCompleted, this.areTranslationsInProgress);
    }
    get isOverallCompleted() {
        var _a, _b;
        return ((_b = (_a = this.projectPages) === null || _a === void 0 ? void 0 : _a.isCompleted) !== null && _b !== void 0 ? _b : false) && !this.areTranslationsInProgress;
    }
    get studyProgressCompleted() {
        var _a, _b;
        return (_b = (_a = this.projectPages) === null || _a === void 0 ? void 0 : _a.isCompleted) !== null && _b !== void 0 ? _b : false;
    }
    get isProjectSettingsCompleted() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.projectPages) === null || _a === void 0 ? void 0 : _a.pages.find((page) => page.pageName === ProjectPageName.PROJECT_SETTINGS)) === null || _b === void 0 ? void 0 : _b.isCompleted) !== null && _c !== void 0 ? _c : false;
    }
    getIsPatientExperienceCompleted(projectPages) {
        var _a, _b;
        return (_b = (_a = projectPages === null || projectPages === void 0 ? void 0 : projectPages.pages.find((page) => page.pageName === ProjectPageName.PATIENT_EXPERIENCE)) === null || _a === void 0 ? void 0 : _a.isCompleted) !== null && _b !== void 0 ? _b : false;
    }
    get isPatientExperienceCompleted() {
        return this.getIsPatientExperienceCompleted(this.projectPages);
    }
    get studyContainsUnReviewedContent() {
        var _a;
        if (this.study === NOT_LOADED || this.study === null)
            return null;
        return (_a = this.study.icfTextSegments) === null || _a === void 0 ? void 0 : _a.some((segment) => !segment.reviewed);
    }
    get studyStatus() {
        if (this.study === NOT_LOADED || this.study === null)
            return null;
        return this.study.status;
    }
    get myStudyUser() {
        return this.getMyStudyUser(this.study);
    }
    get studyId() {
        var _a, _b, _c;
        return this.isRelevantRoute
            ? (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.projectId) !== null && _b !== void 0 ? _b : (_c = this.routingUIManager.currentRouteParams) === null || _c === void 0 ? void 0 : _c.studyId
            : undefined;
    }
    get isRelevantRoute() {
        return !!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [NamedRoutes.PROJECTS_ADD_PROJECT]);
    }
    get currentPage() {
        var _a;
        if (!this.isRelevantRoute)
            return null;
        const rawPage = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.page;
        if (!rawPage)
            return null;
        const page = unslugify(rawPage);
        const projectPage = asEnum(ProjectPageName)(page !== null && page !== void 0 ? page : "");
        if (projectPage) {
            return projectPage;
        }
        const projectStaticPage = asEnum(ProjectStaticPageName)(page !== null && page !== void 0 ? page : "");
        if (projectStaticPage) {
            return projectStaticPage;
        }
        return null;
    }
    get currentPageIndex() {
        if (this.currentProjectPageNameRoute === null || this.projectDataPages === null)
            return null;
        return this.projectDataPages.pages.findIndex((page) => page.pageName === this.currentProjectPageNameRoute);
    }
    get currentProjectPageNameRoute() {
        var _a;
        return asEnum(ProjectPageName)((_a = this.currentPage) !== null && _a !== void 0 ? _a : "");
    }
    get currentProjectPage() {
        var _a, _b;
        return (_b = (_a = this.projectPages) === null || _a === void 0 ? void 0 : _a.pages.find((page) => page.pageName === this.currentProjectPageNameRoute)) !== null && _b !== void 0 ? _b : null;
    }
    get currentProjectSection() {
        var _a;
        if (this.currentProjectPage === null || this.currentProjectSectionName === null)
            return null;
        return (_a = this.currentProjectPage.sections.find((section) => section.sectionName === this.currentProjectSectionName)) !== null && _a !== void 0 ? _a : null;
    }
    get isSponsoredStudy() {
        var _a;
        if (this.study === NOT_LOADED || this.study === null)
            return false;
        return (_a = this.study.isSponsor) !== null && _a !== void 0 ? _a : false;
    }
    get needsIdentifiedData() {
        if (this.study === NOT_LOADED || this.study === null)
            return false;
        return this.study.accessData === StudyAccessData.FULL_RECORD_IDENTIFIED;
    }
    get selectedLanguageTranslationsCompleted() {
        var _a;
        return ((_a = this.selectedLanguageSectionTranslationProgressInfo) === null || _a === void 0 ? void 0 : _a.progress) === 100;
    }
    get allTranslationsCompleted() {
        var _a;
        return (_a = this.allLanguageSectionTranslationProgressInfo) === null || _a === void 0 ? void 0 : _a.every((info) => info.progress === 100);
    }
    get translationsCompletedLookup() {
        var _a, _b;
        return ((_b = (_a = this.allLanguageSectionTranslationProgressInfo) === null || _a === void 0 ? void 0 : _a.reduce((acc, info) => {
            if (info.language !== StudyTranslationLanguage.en) {
                acc[info.language] = info.progress === 100;
            }
            return acc;
        }, {})) !== null && _b !== void 0 ? _b : {});
    }
    get isInterventionalStudy() {
        if (this.study === NOT_LOADED || this.study === null)
            return false;
        return this.study.studyType === StudyType.INTERVENTIONAL;
    }
    get organization() {
        const org = this.organizationsManager.myOrganization.value;
        if (!org || org === NOT_LOADED)
            return null;
        return checkUserType(org, UserType.HEALTHCARE_ORGANIZATION, UserType.EXTERNAL_RESEARCHER);
    }
    get organizationRequire21CFRPart11ForInformedConsents() {
        var _a, _b;
        return (_b = (_a = this.organization) === null || _a === void 0 ? void 0 : _a.require21CFRPart11ForInformedConsents) !== null && _b !== void 0 ? _b : false;
    }
    get organizationDefaultRequire21CFRPart11ForInformedConsents() {
        var _a, _b;
        return (_b = (_a = this.organization) === null || _a === void 0 ? void 0 : _a.defaultRequire21CFRPart11ForInformedConsents) !== null && _b !== void 0 ? _b : false;
    }
    setCurrentTab(tab) {
        this.currentTab = tab;
    }
    async updateVisitedPageIfNecessary() {
        var _a, _b;
        const pageName = this.currentProjectPageNameRoute;
        if (!pageName)
            return;
        if (this.study === NOT_LOADED || this.study === null)
            return;
        this.hasPageBeenVisited = (_b = (_a = this.study.visitedPages) === null || _a === void 0 ? void 0 : _a.includes(pageName)) !== null && _b !== void 0 ? _b : false;
        if (this.hasPageBeenVisited)
            return;
        await this.autoSaveStudy({ addVisitedPages: [pageName] }, true);
    }
    async updateVisitedSectionIfNecessary() {
        var _a, _b;
        const sectionName = this.currentProjectSectionName;
        if (sectionName === null || !isEnum(ProjectSectionName)(sectionName))
            return;
        if (this.study === NOT_LOADED || this.study === null)
            return;
        this.hasSectionBeenVisited = (_b = (_a = this.study.visitedSections) === null || _a === void 0 ? void 0 : _a.includes(sectionName)) !== null && _b !== void 0 ? _b : false;
        if (this.hasSectionBeenVisited)
            return;
        await this.updateVisitedSections([sectionName]);
    }
    async updateVisitedSections(sectionNames, waitForPageScrollToSection = false) {
        if (!sectionNames || sectionNames.length === 0)
            return;
        if (waitForPageScrollToSection && this.isScrollingToPageSection)
            return;
        await this.autoSaveStudy({ addVisitedSections: sectionNames }, true);
    }
    setCurrentSection(sectionName) {
        this.currentProjectSectionName = sectionName;
    }
    redirectToStudyManagerIfNecessary() {
        if (!this.isRelevantRoute)
            return;
        if (this.studyExistsButHasNoDraft === true) {
            this.routingUIManager.navigateTo(NamedRoutes.PROJECTS_PROJECT_MANAGER, {
                projectId: this.studyId,
            });
        }
    }
    get navSectionOffset() {
        return -(this.topBannerHeight + navSectionExtraOffset);
    }
    async goToPage(pageName, sectionName, studyId) {
        this.routingUIManager.navigateTo(NamedRoutes.PROJECTS_ADD_PROJECT, {
            projectId: studyId !== null && studyId !== void 0 ? studyId : this.studyId,
            page: slugify(pageName),
        });
        if (sectionName) {
            this.updateLastSectionOffset(null);
            this.isScrollingToPageSection = true;
            await when(() => this.currentProjectPageNameRoute === pageName);
            scroller.scrollTo(navSectionId.replace(":sectionName", sectionName), {
                containerId: navSectionsContainerId,
                duration: 0,
                offset: this.navSectionOffset,
            });
            await when(() => this.currentProjectSectionName === sectionName);
            this.isScrollingToPageSection = false;
        }
    }
    async goToPreviousPage() {
        if (this.currentPage === ProjectStaticPageName.PREVIEW) {
            await this.goToPage(ProjectStaticPageName.TRANSLATIONS);
            return;
        }
        else if (this.currentPage === ProjectStaticPageName.TRANSLATIONS) {
            if (this.needsIdentifiedData) {
                await this.goToPage(ProjectPageName.PATIENT_EXPERIENCE);
            }
            else {
                await this.goToPage(ProjectPageName.INFORMATION_FOR_PATIENTS);
            }
            return;
        }
        if (this.currentPageIndex === null || this.projectDataPages === null)
            return;
        if (this.currentPageIndex <= 0)
            return;
        await this.goToPage(this.projectDataPages.pages[this.currentPageIndex - 1].pageName);
    }
    async goToNextPage() {
        if (this.currentPage === ProjectStaticPageName.TRANSLATIONS) {
            await this.goToPage(ProjectStaticPageName.PREVIEW);
            return;
        }
        else if (this.currentPage === ProjectPageName.PATIENT_EXPERIENCE ||
            this.currentPage === ProjectPageName.INFORMATION_FOR_PATIENTS) {
            await this.goToPage(ProjectStaticPageName.TRANSLATIONS);
            return;
        }
        if (this.currentPageIndex === null || this.projectDataPages === null)
            return;
        if (this.currentPageIndex === this.projectDataPages.pages.length - 1)
            return;
        await this.goToPage(this.projectDataPages.pages[this.currentPageIndex + 1].pageName);
    }
    async saveAndExit() {
        var _a;
        await this.saveStudyIfNeeded();
        const savedPage = this.currentPage;
        const myStudyUser = this.myStudyUser;
        if (this.studyId && savedPage && myStudyUser !== null) {
            try {
                await this.studyManager.createOrUpdateStudyUser(this.studyId, myStudyUser.user, {
                    savedPage,
                });
            }
            catch (e) {
                console.error("failed to save current page");
            }
        }
        if (this.study !== NOT_LOADED && ((_a = this.study) === null || _a === void 0 ? void 0 : _a.status) === StudyStatus.ACTIVE) {
            this.routingUIManager.navigateTo(NamedRoutes.PROJECTS_PROJECT_MANAGER, {
                projectId: this.studyId,
            });
        }
        else {
            this.routingUIManager.navigateTo(NamedRoutes.PROJECTS);
        }
    }
    async saveStudyToImport(studyToImport) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        if (studyToImport.importMethod === StudyImportMethod.MANUAL) {
            this.studyFormModel.updateInput({ importMethod: StudyImportMethod.MANUAL });
            return;
        }
        const importInput = {
            importMethod: studyToImport.importMethod,
            briefSummary: (_a = studyToImport.briefSummary) !== null && _a !== void 0 ? _a : "",
            description: (_b = studyToImport.description) !== null && _b !== void 0 ? _b : "",
            clinicalTrialPhase: (_c = studyToImport.clinicalTrialPhase) !== null && _c !== void 0 ? _c : null,
            primaryDiseaseOrConditions: studyToImport.primaryDiseaseOrConditions,
            interventions: studyToImport.interventions,
            outcomes: (_d = studyToImport.outcomes) !== null && _d !== void 0 ? _d : "",
            inclusionExclusionCriteria: (_e = studyToImport.inclusionExclusionCriteria) !== null && _e !== void 0 ? _e : "",
            targetNumberOfPatients: studyToImport.targetNumberOfPatients !== undefined && studyToImport.targetNumberOfPatients !== null
                ? this.numberToString(studyToImport.targetNumberOfPatients)
                : null,
            startDate: studyToImport.startDate ? this.dateToString(studyToImport.startDate) : null,
            completionDate: studyToImport.completionDate ? this.dateToString(studyToImport.completionDate) : null,
            primaryCompletionDate: studyToImport.primaryCompletionDate ? this.dateToString(studyToImport.primaryCompletionDate) : null,
            sponsorOrganizationName: (_f = studyToImport.sponsorOrganizationName) !== null && _f !== void 0 ? _f : "",
            sponsorPrimaryContactName: (_g = studyToImport.sponsorPrimaryContactName) !== null && _g !== void 0 ? _g : "",
            sponsorPrimaryContactTitle: (_h = studyToImport.sponsorPrimaryContactTitle) !== null && _h !== void 0 ? _h : "",
            sponsorPrimaryContactEmail: (_j = studyToImport.sponsorPrimaryContactEmail) !== null && _j !== void 0 ? _j : "",
            sponsorPrimaryContactPhone: (_k = studyToImport.sponsorPrimaryContactPhone) !== null && _k !== void 0 ? _k : "",
            sponsorPrimaryContactPhoneExt: (_l = studyToImport.sponsorPrimaryContactPhoneExt) !== null && _l !== void 0 ? _l : "",
            funderType: studyToImport.funderType,
            studyIdentifier: (_m = studyToImport.studyIdentifier) !== null && _m !== void 0 ? _m : "",
            webpageLink: (_o = studyToImport.webpageLink) !== null && _o !== void 0 ? _o : "",
            defaultRequire21CFRPart11ForInformedConsents: null,
            studyType: (_p = studyToImport.studyType) !== null && _p !== void 0 ? _p : undefined,
            accessData: (_q = studyToImport.accessData) !== null && _q !== void 0 ? _q : undefined,
            studyProtocolFileName: (_r = studyToImport.studyProtocolFileName) !== null && _r !== void 0 ? _r : undefined,
            investigationType: studyToImport.investigationType === StudyInvestigationType.NEITHER ? null : studyToImport.investigationType,
        };
        this.studyFormModel.updateInput(importInput);
    }
    async updateStudyWithUploadedICFData(patientFacingDetails) {
        var _a, _b;
        const transformedInput = { ...patientFacingDetails };
        if (patientFacingDetails.studySectionImages) {
            const imageGroups = new Map();
            for (const img of patientFacingDetails.studySectionImages) {
                const sectionName = img.name;
                if (!imageGroups.has(sectionName)) {
                    imageGroups.set(sectionName, []);
                }
                imageGroups.get(sectionName).push({
                    imageUrl: (_a = img.imageUrl) !== null && _a !== void 0 ? _a : "",
                    caption: (_b = img.caption) !== null && _b !== void 0 ? _b : "",
                    sectionName: img.name,
                    sectionType: StudySectionType.DRAFT,
                    status: StudySectionImageStatus.DRAFT,
                    language: StudyTranslationLanguage.en,
                    order: patientFacingDetails.studySectionImages.indexOf(img),
                });
            }
            transformedInput.studySectionImages = Array.from(imageGroups.entries()).map(([sectionName, images]) => ({
                sectionName,
                images,
            }));
        }
        await this.autoSaveStudy(transformedInput, true);
    }
    async updateStudyWithTranslationImport(studyTranslatableFields, outOfDateImportedTranslations, isSectionUpdate) {
        var _a, _b, _c, _d, _e;
        if (!this.study || this.study === NOT_LOADED)
            return;
        const studyId = this.studyId;
        const orgId = this.authManager.orgId;
        if (!studyId || !orgId)
            return;
        this.translationsAddedThisSession = true;
        const additionalSections = (_a = studyTranslatableFields.additionalSections) !== null && _a !== void 0 ? _a : [];
        const additionalSectionsUpdate = await this.getAdditionalSectionsUpdate(additionalSections.length > 0
            ? {
                additionalSections: [...((_b = this.study.additionalSections) !== null && _b !== void 0 ? _b : []), ...additionalSections],
            }
            : {});
        await this.studyService.updateStudyVersion(studyId, orgId, {
            studyTranslatableFields: [studyTranslatableFields.translatableFields],
            ...(additionalSectionsUpdate ? { additionalSections: additionalSectionsUpdate } : {}),
        });
        const sectionImages = (_c = studyTranslatableFields.sectionImages) !== null && _c !== void 0 ? _c : [];
        if (sectionImages.length) {
            const existingTranslationImages = (_e = (_d = this.study.studySectionImages) === null || _d === void 0 ? void 0 : _d.flatMap((group) => group.images.filter((img) => img.language === studyTranslatableFields.language))) !== null && _e !== void 0 ? _e : [];
            const createSectionImages = sectionImages
                .filter((section) => {
                if (section.imageId)
                    return false;
                if (!section.translatedFrom)
                    return false;
                return !existingTranslationImages.some((existing) => existing.translatedFrom === section.translatedFrom);
            })
                .map((section) => ({
                imageUrl: "",
                caption: section.caption,
                sectionName: section.sectionName,
                language: studyTranslatableFields.language,
                status: StudySectionImageStatus.ACTIVE,
                order: section.order,
                sectionType: StudySectionType.REGULAR,
                additionalSectionId: section.additionalSectionId,
                translatedFrom: section.translatedFrom,
            }));
            if (createSectionImages.length) {
                await this.studySectionImageService.createStudySectionImageBatch(orgId, studyId, {
                    images: createSectionImages,
                });
            }
            const updateSectionImages = sectionImages
                .filter((section) => !!section.imageId)
                .map((section) => ({
                id: section.imageId,
                caption: section.caption,
                additionalSectionId: section.additionalSectionId,
                translatedFrom: section.translatedFrom,
            }));
            const existingImagesToUpdate = sectionImages
                .filter((section) => {
                if (section.imageId)
                    return false;
                if (!section.translatedFrom)
                    return false;
                return existingTranslationImages.some((existing) => existing.translatedFrom === section.translatedFrom);
            })
                .map((section) => {
                const existingImage = existingTranslationImages.find((existing) => existing.translatedFrom === section.translatedFrom);
                return {
                    id: existingImage.id,
                    caption: section.caption,
                    additionalSectionId: section.additionalSectionId,
                    translatedFrom: section.translatedFrom,
                };
            });
            const allUpdateImages = [...updateSectionImages, ...existingImagesToUpdate];
            if (allUpdateImages.length) {
                await this.studySectionImageService.updateStudySectionImageBatch(orgId, studyId, {
                    images: allUpdateImages,
                });
            }
        }
        if (isSectionUpdate) {
            Object.keys(studyTranslatableFields.translatableFields).forEach((key) => {
                delete this.outOfDateImportedTranslations[key];
            });
            additionalSections.forEach((section) => {
                delete this.outOfDateImportedTranslations[`additionalInformation_${section.translatedFrom}`];
            });
            sectionImages.forEach((section) => {
                delete this.outOfDateImportedTranslations[`sectionImages_${section.translatedFrom}`];
            });
        }
        else {
            this.outOfDateImportedTranslations = outOfDateImportedTranslations;
        }
        await this.refreshStudy({ preserveDirty: true });
    }
    cleanupConfirmAndAddStudyLocalForm() {
        this.confirmAndAddStudyForm.input.confirmed = false;
    }
    getMyStudyUser(study) {
        var _a;
        if (study === NOT_LOADED || study === null)
            return null;
        return (_a = study.users.find((user) => user.user.id === this.authManager.id)) !== null && _a !== void 0 ? _a : null;
    }
    async getICFProtocolDocumentDownloadUrl(inline) {
        var _a, _b;
        if (this.study === NOT_LOADED || !((_a = this.study) === null || _a === void 0 ? void 0 : _a.icfFileName))
            return null;
        if (this.organization === null)
            return null;
        const fileUploadInfo = await this.protocolIngestionService.getICFFormDownloadUrl(this.organization.id, this.study.id, (_b = this.study) === null || _b === void 0 ? void 0 : _b.icfFileName, inline, this.study.status === StudyStatus.DRAFT);
        return fileUploadInfo.signedUrl;
    }
    async getStudyProtocolDocumentDownloadUrl(inline) {
        var _a, _b;
        if (this.study === NOT_LOADED || !((_a = this.study) === null || _a === void 0 ? void 0 : _a.studyProtocolFileName))
            return null;
        if (this.organization === null)
            return null;
        const fileUploadInfo = await this.protocolIngestionService.getStudyProtocolDownloadUrl(this.organization.id, this.study.id, (_b = this.study) === null || _b === void 0 ? void 0 : _b.studyProtocolFileName, inline, this.study.status === StudyStatus.DRAFT);
        return fileUploadInfo.signedUrl;
    }
    calculateInformedConsentRequiredFields(data) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        if (data === null) {
            return {
                totalExtractedFields: 0,
                emptyRequiredFields: (_c = (_b = (_a = this.projectPages) === null || _a === void 0 ? void 0 : _a.requiredInformedConsentFieldNames) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0,
            };
        }
        const allRelevantFields = [
            ...((_e = (_d = this.projectPages) === null || _d === void 0 ? void 0 : _d.requiredInformedConsentFieldNames) !== null && _e !== void 0 ? _e : []),
            ...((_g = (_f = this.projectPages) === null || _f === void 0 ? void 0 : _f.optionalInformedConsentFieldNames) !== null && _g !== void 0 ? _g : []),
        ];
        let totalExtractedFields = 0;
        allRelevantFields.forEach((field) => {
            const value = data[field];
            if (typeof value === "boolean") {
                totalExtractedFields++;
            }
            else if (typeof value === "string") {
                if (value.trim() !== "") {
                    totalExtractedFields++;
                }
            }
            else if (Array.isArray(value)) {
                if (value.length > 0) {
                    totalExtractedFields++;
                }
            }
        });
        return {
            totalExtractedFields,
            emptyRequiredFields: (_j = (_h = this.projectPages) === null || _h === void 0 ? void 0 : _h.emptyRequiredInformedConsentFields) !== null && _j !== void 0 ? _j : 0,
        };
    }
    get isInformedConsentFilled() {
        var _a, _b;
        return (_b = (_a = this.projectPages) === null || _a === void 0 ? void 0 : _a.isAtLeastOneInformedConsentFieldFilled) !== null && _b !== void 0 ? _b : false;
    }
    get showStudyDetailsDialogForFirstTimeUser() {
        var _a;
        if (this.study === NOT_LOADED || this.study === null || this.currentProjectPageNameRoute === null || !this.isResearchProject)
            return false;
        if (this.currentProjectPageNameRoute !== ProjectPageName.PROJECT_DETAILS &&
            this.currentProjectPageNameRoute !== ProjectPageName.PROJECT_SETTINGS)
            return false;
        const hasVisitedProjectDetailsPage = (_a = this.study.visitedPages) === null || _a === void 0 ? void 0 : _a.some((page) => page === ProjectPageName.PROJECT_DETAILS || page === ProjectPageName.PROJECT_SETTINGS);
        return !hasVisitedProjectDetailsPage;
    }
    async confirmAndAddStudy(versionName) {
        if (this.study === NOT_LOADED || this.study === null || !this.authManager.orgId)
            return;
        if (this.isAddingAndConfirmingStudy)
            return;
        this.isAddingAndConfirmingStudy = true;
        try {
            await this.saveStudyIfNeeded();
            const firstTimeActivation = this.study.status === StudyStatus.DRAFT;
            await this.studyService.updateStudyVersion(this.study.id, this.authManager.orgId, {
                versionName,
                versionStatus: StudyVersionStatus.ACTIVE,
            });
            this.dialogUIManager.displayDialog({
                open: true,
                onClose: () => { },
                title: "",
                closeChoice: false,
                buttons: [[false, firstTimeActivation ? "Get started" : "Done"]],
                footerHasTopBorder: true,
                width: 680,
                children: React.createElement(AddStudyPostActivateDialogContent, { firstTimeActivation }),
            });
            this.routingUIManager.navigateTo(NamedRoutes.PROJECTS_PROJECT_MANAGER, {
                projectId: this.study.id,
            });
        }
        catch (e) {
            console.error("Error adding and confirming project", e);
        }
        finally {
            this.isAddingAndConfirmingStudy = false;
            this.persistence.removeEnglishStringsDownloaded(this.study.id);
        }
    }
    async deleteStudyDraft() {
        if (this.study === NOT_LOADED || this.study === null)
            return;
        await this.studyManager.deleteStudyDraft(this.study);
        this.persistence.removeEnglishStringsDownloaded(this.study.id);
        if (this.study.status === StudyStatus.ACTIVE) {
            this.routingUIManager.navigateTo(NamedRoutes.PROJECTS_PROJECT_MANAGER, {
                projectId: this.studyId,
            });
        }
        else {
            this.routingUIManager.navigateTo(NamedRoutes.PROJECTS);
        }
    }
    initiateEnrollmentVideoUploadingProgress() {
        this.enrollmentVideoUploadingInfo = { progress: 0 };
    }
    async showInformedConsentLockedIfAppropriate() {
        if (this.study === NOT_LOADED || this.study === null)
            return;
        if (this.currentPage &&
            (this.currentPage === ProjectPageName.PATIENT_EXPERIENCE || this.currentPage === ProjectPageName.INFORMATION_FOR_PATIENTS) &&
            this.informedConsentSectionLockedByOtherUser) {
            const choice = await this.informedConsentLockedDialogModel.showAndWait();
            if (choice === "GO_TO_OVERVIEW") {
                await this.goToPage(ProjectPageName.PROJECT_DETAILS);
            }
            else if (choice === "SAVE_AND_EXIT") {
                await this.saveAndExit();
            }
        }
    }
    get shouldInformedConsentBeLockedByUs() {
        return false;
    }
    async showMajorChangeWarningDialogIfAppropriate(newStudyDto, oldStudyDto, justSavedKeys) {
        if (oldStudyDto === NOT_LOADED || oldStudyDto === null)
            return;
        let dialogResponse;
        if (newStudyDto.status !== StudyStatus.DRAFT && oldStudyDto.status === StudyStatus.DRAFT) {
            this.majorChangesDialogModel.changesType = "COMPLETED_ADDING";
            dialogResponse = await this.majorChangesDialogModel.showAndWait();
        }
        else if (newStudyDto.icfFileName &&
            !oldStudyDto.icfFileName &&
            !justSavedKeys.includes("icfFileName") &&
            !this.wasInformedConsentLockedByUs) {
            this.majorChangesDialogModel.changesType = "ICF_UPLOADED";
            dialogResponse = await this.majorChangesDialogModel.showAndWait();
        }
        else if (newStudyDto.icfFileName !== oldStudyDto.icfFileName &&
            newStudyDto.icfFileName &&
            !justSavedKeys.includes("icfFileName") &&
            !this.wasInformedConsentLockedByUs) {
            this.majorChangesDialogModel.changesType = "NEW_ICF_UPLOADED";
            dialogResponse = await this.majorChangesDialogModel.showAndWait();
        }
        else {
            dialogResponse = null;
        }
        if (dialogResponse === "RELOAD") {
            await this.refreshStudy({ fullRefresh: true });
        }
        else if (dialogResponse === "GOTO_STUDY_MANAGER") {
            this.routingUIManager.navigateTo(NamedRoutes.PROJECTS_PROJECT_MANAGER, { projectId: newStudyDto.id });
        }
    }
    cleanupDialogs() {
        this.informedConsentLockedDialogModel.open = false;
        this.majorChangesDialogModel.open = false;
    }
    deleteStudyProtocolFile(fileName, protocolType) {
        var _a;
        if (this.study !== NOT_LOADED && ((_a = this.study) === null || _a === void 0 ? void 0 : _a.id) && this.authManager.orgId && fileName) {
            this.protocolIngestionService.deleteProtocolUploadedDocument(this.authManager.orgId, this.study.id, fileName, protocolType, {
                deleteFromTemp: true,
            });
        }
    }
    updateTopBannerHeight(height) {
        this.topBannerHeight = height;
    }
    updateLastSectionOffset(offset) {
        this.lastSectionOffset = offset;
    }
    get bottomPaddingForPage() {
        if (!this.currentProjectPageNameRoute)
            return 0;
        if (this.lastSectionOffset === null)
            return null;
        return Math.max(0, this.lastSectionOffset - headerAndFooterHeightRaw * 2 - addProjectPagesParentPaddingBottom - navSectionExtraOffset);
    }
    get detectedEnglishTranslationUpdate() {
        return this.allLanguageSectionTranslationProgressInfo.some((info) => info.outdatedSections.length > 0);
    }
    getAllLanguageSectionTranslationProgressInfo(study, projectPages) {
        var _a;
        if (!study)
            return [];
        const patientExperienceSections = (_a = projectPages === null || projectPages === void 0 ? void 0 : projectPages.pages.find((page) => page.pageName === ProjectPageName.PATIENT_EXPERIENCE)) === null || _a === void 0 ? void 0 : _a.sections;
        if (!patientExperienceSections)
            return [];
        return filterNull(study.studyTranslatableFields
            .filter((fields) => fields.language !== StudyTranslationLanguage.en)
            .map((fields) => this.getSectionTranslationProgressInfo(study, patientExperienceSections, fields.language)));
    }
    get allLanguageSectionTranslationProgressInfo() {
        if (this.study === NOT_LOADED || this.study === null)
            return [];
        return this.getAllLanguageSectionTranslationProgressInfo(this.study, this.projectPages);
    }
    get selectedLanguageSectionTranslationProgressInfo() {
        var _a;
        return (_a = this.allLanguageSectionTranslationProgressInfo.find((info) => info.language === this.selectedTranslationLanguage)) !== null && _a !== void 0 ? _a : null;
    }
    get hasTranslations() {
        if (!this.study || this.study === NOT_LOADED)
            return false;
        return this.allLanguageSectionTranslationProgressInfo.some((info) => info.enabled !== undefined);
    }
    getRawAreTranslationsInProgress(study, projectPages) {
        const progressInfo = this.getAllLanguageSectionTranslationProgressInfo(study, projectPages);
        return this.getAreTranslationsInProgress(progressInfo);
    }
    getAreTranslationsInProgress(progressInfo) {
        return progressInfo.some((info) => info.progress !== 100 && info.enabled === true);
    }
    get areTranslationsInProgress() {
        if (!this.study || this.study === NOT_LOADED)
            return false;
        return this.getAreTranslationsInProgress(this.allLanguageSectionTranslationProgressInfo);
    }
    get hasTranslationsAndAreCompleted() {
        return this.hasTranslations && !this.areTranslationsInProgress;
    }
    getSectionTranslationProgressInfo(study, patientExperienceSections, language) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6;
        const studyCopy = deepCopy(study);
        const availableSections = getTranslatableSectionsFromProjectSections(patientExperienceSections);
        const availableTranslatableFields = getTranslatableFieldsFromProjectSections(availableSections, false);
        const englishTranslations = getTranslatableFieldsFromStudy(studyCopy, availableTranslatableFields, StudyTranslationLanguage.en);
        const translations = getTranslatableFieldsFromStudy(studyCopy, availableTranslatableFields, language);
        const allCurrentSectionImages = (_b = (_a = studyCopy.studySectionImages) === null || _a === void 0 ? void 0 : _a.map((section) => section.images).flat()) !== null && _b !== void 0 ? _b : [];
        const englishAdditionalSections = (_d = (_c = studyCopy.additionalSections) === null || _c === void 0 ? void 0 : _c.filter((section) => section.language === StudyTranslationLanguage.en)) !== null && _d !== void 0 ? _d : [];
        const englishDiseaseAndConditions = (_e = englishTranslations === null || englishTranslations === void 0 ? void 0 : englishTranslations.informedConsentPrimaryDiseaseOrConditions) !== null && _e !== void 0 ? _e : [];
        const englishTimelineVisits = (_f = englishTranslations === null || englishTranslations === void 0 ? void 0 : englishTranslations.timelineVisits) !== null && _f !== void 0 ? _f : [];
        const allEnglishSectionImages = allCurrentSectionImages.filter((image) => image.language === StudyTranslationLanguage.en);
        const englishCompositeUpdatedAt = (_j = (_h = (_g = studyCopy.studyTranslatableFields) === null || _g === void 0 ? void 0 : _g.find((field) => field.language === StudyTranslationLanguage.en)) === null || _h === void 0 ? void 0 : _h.compositeUpdatedAt) !== null && _j !== void 0 ? _j : {};
        const translationsAdditionalSections = (_l = (_k = studyCopy.additionalSections) === null || _k === void 0 ? void 0 : _k.filter((section) => section.language === language)) !== null && _l !== void 0 ? _l : [];
        const translationsDiseaseAndConditions = (_m = translations === null || translations === void 0 ? void 0 : translations.informedConsentPrimaryDiseaseOrConditions) !== null && _m !== void 0 ? _m : [];
        const translationsTimelineVisits = (_o = translations === null || translations === void 0 ? void 0 : translations.timelineVisits) !== null && _o !== void 0 ? _o : [];
        const translationCompositeUpdatedAt = (_r = (_q = (_p = studyCopy.studyTranslatableFields) === null || _p === void 0 ? void 0 : _p.find((field) => field.language === language)) === null || _q === void 0 ? void 0 : _q.compositeUpdatedAt) !== null && _r !== void 0 ? _r : {};
        const allTranslationsSectionImages = allCurrentSectionImages.filter((image) => image.language === language);
        const englishEnrollmentVideo = (_s = studyCopy === null || studyCopy === void 0 ? void 0 : studyCopy.enrollmentTranscode) !== null && _s !== void 0 ? _s : null;
        const translatedFields = (_t = studyCopy.studyTranslatableFields) === null || _t === void 0 ? void 0 : _t.find((field) => field.language === language);
        const translatedEnrollmentVideo = (_u = translatedFields === null || translatedFields === void 0 ? void 0 : translatedFields.enrollmentVideo) !== null && _u !== void 0 ? _u : null;
        const incompleteSections = [];
        const copiedImagesVideosSections = [];
        const outdatedSections = [];
        const hasTranslationsSections = [];
        const generatedWithAISections = [];
        const manuallyEditedSections = [];
        const sectionLanguageInfo = {};
        const translationInput = {};
        let mostRecentTranslationUpdatedAt = new Date(0);
        const isDateAfterMostRecentTranslation = (date) => {
            return isAfter(new Date(date), new Date(mostRecentTranslationUpdatedAt));
        };
        if (englishTranslations && translations) {
            const translationSectionAdditionalSections = [];
            for (const section of availableSections) {
                const { sectionName, fields } = section;
                const sectionInfo = {
                    english: {},
                };
                const propertyKeys = fields.flatMap((field) => { var _a; return (_a = field.translatableFieldNames) !== null && _a !== void 0 ? _a : []; });
                let completed = true;
                let outdated = false;
                let hasTranslations = false;
                let manuallyEdited = false;
                const translationFieldsWithMetaData = (_v = study.studyTranslatableFields) === null || _v === void 0 ? void 0 : _v.find((field) => field.language === language);
                const translationImportType = translationFieldsWithMetaData === null || translationFieldsWithMetaData === void 0 ? void 0 : translationFieldsWithMetaData.importType;
                const translationImportTypeUpdatedAt = translationFieldsWithMetaData === null || translationFieldsWithMetaData === void 0 ? void 0 : translationFieldsWithMetaData.importTypeUpdatedAt;
                const translationSectionInfo = (_w = translationFieldsWithMetaData === null || translationFieldsWithMetaData === void 0 ? void 0 : translationFieldsWithMetaData.sectionInfo) === null || _w === void 0 ? void 0 : _w.find((info) => info.section === sectionName);
                const translationSectionAiImportUpdatedAt = translationSectionInfo === null || translationSectionInfo === void 0 ? void 0 : translationSectionInfo.aiImportUpdatedAt;
                const isManualDateAfterAIGeneratedDate = (date) => {
                    if (translationSectionAiImportUpdatedAt) {
                        return isAfter(new Date(date), new Date(translationSectionAiImportUpdatedAt));
                    }
                    if (translationImportType === StudyTranslationImportType.AI_GENERATION && translationImportTypeUpdatedAt) {
                        return isAfter(new Date(date), new Date(translationImportTypeUpdatedAt));
                    }
                    return false;
                };
                if (translationImportType === StudyTranslationImportType.AI_GENERATION || !!translationSectionInfo) {
                    generatedWithAISections.push(sectionName);
                }
                const sectionNameKeyParts = sectionName.split("_");
                for (const propertyKey of propertyKeys) {
                    if (propertyKey === "additionalInformation" && sectionNameKeyParts[2]) {
                        const englishSectionAdditionSections = [];
                        const additionalSectionIndex = parseInt(sectionNameKeyParts[2]) - 1;
                        if (englishAdditionalSections.length <= additionalSectionIndex)
                            continue;
                        const englishAdditionalSection = englishAdditionalSections[additionalSectionIndex];
                        const translatedAdditionalSection = translationsAdditionalSections === null || translationsAdditionalSections === void 0 ? void 0 : translationsAdditionalSections.find((s) => s.translatedFrom === englishAdditionalSection.id);
                        const translationExists = translatedAdditionalSection !== undefined &&
                            !!translatedAdditionalSection.title &&
                            !!translatedAdditionalSection.description;
                        if (!translationExists) {
                            completed = false;
                        }
                        hasTranslations = !!(translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.title) || !!(translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.description);
                        if ((englishAdditionalSection.updatedTitleAt &&
                            (translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.updatedTitleAt) &&
                            englishAdditionalSection.updatedTitleAt > translatedAdditionalSection.updatedTitleAt) ||
                            (englishAdditionalSection.updatedDescriptionAt &&
                                (translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.updatedDescriptionAt) &&
                                englishAdditionalSection.updatedDescriptionAt > translatedAdditionalSection.updatedDescriptionAt) ||
                            this.outOfDateImportedTranslations[`additionalInformation_${englishAdditionalSection.id}`]) {
                            outdated = true;
                        }
                        if ((translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.updatedTitleAt) &&
                            isDateAfterMostRecentTranslation(translatedAdditionalSection.updatedTitleAt)) {
                            mostRecentTranslationUpdatedAt = translatedAdditionalSection.updatedTitleAt;
                        }
                        if ((translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.updatedDescriptionAt) &&
                            isDateAfterMostRecentTranslation(translatedAdditionalSection.updatedDescriptionAt)) {
                            mostRecentTranslationUpdatedAt = translatedAdditionalSection.updatedDescriptionAt;
                        }
                        if (((translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.updatedTitleAt) &&
                            isManualDateAfterAIGeneratedDate(translatedAdditionalSection.updatedTitleAt)) ||
                            ((translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.updatedDescriptionAt) &&
                                isManualDateAfterAIGeneratedDate(translatedAdditionalSection.updatedDescriptionAt))) {
                            manuallyEdited = true;
                        }
                        englishSectionAdditionSections.push(englishAdditionalSection);
                        translationSectionAdditionalSections.push(translatedAdditionalSection !== null && translatedAdditionalSection !== void 0 ? translatedAdditionalSection : {
                            id: crypto.randomUUID(),
                            title: "",
                            description: "",
                            included: true,
                            language,
                            translatedFrom: (_x = englishAdditionalSection.id) !== null && _x !== void 0 ? _x : "",
                        });
                        sectionInfo.english.additionalSection = englishAdditionalSection;
                    }
                    else if (propertyKey === "additionalQuestions") {
                        const englishSectionAdditionSections = [];
                        for (const englishAdditionalSection of englishAdditionalSections) {
                            const translatedAdditionalSection = translationsAdditionalSections === null || translationsAdditionalSections === void 0 ? void 0 : translationsAdditionalSections.find((s) => s.translatedFrom === englishAdditionalSection.id);
                            const translationExists = translatedAdditionalSection !== undefined &&
                                !!translatedAdditionalSection.title &&
                                !!translatedAdditionalSection.description;
                            if (!translationExists) {
                                completed = false;
                            }
                            hasTranslations = !!(translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.title) || !!(translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.description);
                            if ((englishAdditionalSection.updatedTitleAt &&
                                (translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.updatedTitleAt) &&
                                englishAdditionalSection.updatedTitleAt > translatedAdditionalSection.updatedTitleAt) ||
                                (englishAdditionalSection.updatedDescriptionAt &&
                                    (translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.updatedDescriptionAt) &&
                                    englishAdditionalSection.updatedDescriptionAt > translatedAdditionalSection.updatedDescriptionAt) ||
                                this.outOfDateImportedTranslations[`additionalInformation_${englishAdditionalSection.id}`]) {
                                outdated = true;
                            }
                            if ((translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.updatedTitleAt) &&
                                isDateAfterMostRecentTranslation(translatedAdditionalSection.updatedTitleAt)) {
                                mostRecentTranslationUpdatedAt = translatedAdditionalSection.updatedTitleAt;
                            }
                            if ((translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.updatedDescriptionAt) &&
                                isDateAfterMostRecentTranslation(translatedAdditionalSection.updatedDescriptionAt)) {
                                mostRecentTranslationUpdatedAt = translatedAdditionalSection.updatedDescriptionAt;
                            }
                            if (((translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.updatedTitleAt) &&
                                isManualDateAfterAIGeneratedDate(translatedAdditionalSection.updatedTitleAt)) ||
                                ((translatedAdditionalSection === null || translatedAdditionalSection === void 0 ? void 0 : translatedAdditionalSection.updatedDescriptionAt) &&
                                    isManualDateAfterAIGeneratedDate(translatedAdditionalSection.updatedDescriptionAt))) {
                                manuallyEdited = true;
                            }
                            englishSectionAdditionSections.push(englishAdditionalSection);
                            translationSectionAdditionalSections.push(translatedAdditionalSection !== null && translatedAdditionalSection !== void 0 ? translatedAdditionalSection : {
                                id: crypto.randomUUID(),
                                title: "",
                                description: "",
                                included: true,
                                language,
                                translatedFrom: (_y = englishAdditionalSection.id) !== null && _y !== void 0 ? _y : "",
                            });
                        }
                        if (englishSectionAdditionSections.length > 0) {
                            sectionInfo.english.additionalQuestions = englishSectionAdditionSections;
                        }
                    }
                    else if (propertyKey === "informedConsentPrimaryDiseaseOrConditions") {
                        const englishSectionDiseaseAndConditions = [];
                        const translationsSectionDiseaseAndConditions = [];
                        for (const [index, englishDiseaseAndCondition] of englishDiseaseAndConditions.entries()) {
                            const translatedDiseaseAndCondition = translationsDiseaseAndConditions[index];
                            const translationExists = !!translatedDiseaseAndCondition;
                            if (englishDiseaseAndCondition) {
                                if (!translationExists) {
                                    completed = false;
                                }
                                else {
                                    hasTranslations = true;
                                }
                                if ((englishCompositeUpdatedAt.informedConsentPrimaryDiseaseOrConditions &&
                                    translationCompositeUpdatedAt.informedConsentPrimaryDiseaseOrConditions &&
                                    englishCompositeUpdatedAt.informedConsentPrimaryDiseaseOrConditions >
                                        translationCompositeUpdatedAt.informedConsentPrimaryDiseaseOrConditions) ||
                                    this.outOfDateImportedTranslations.informedConsentPrimaryDiseaseOrConditions) {
                                    outdated = true;
                                }
                                if (translationCompositeUpdatedAt.informedConsentPrimaryDiseaseOrConditions &&
                                    isDateAfterMostRecentTranslation(translationCompositeUpdatedAt.informedConsentPrimaryDiseaseOrConditions)) {
                                    mostRecentTranslationUpdatedAt = translationCompositeUpdatedAt.informedConsentPrimaryDiseaseOrConditions;
                                }
                                if ((translationCompositeUpdatedAt === null || translationCompositeUpdatedAt === void 0 ? void 0 : translationCompositeUpdatedAt.informedConsentPrimaryDiseaseOrConditions) &&
                                    isManualDateAfterAIGeneratedDate(translationCompositeUpdatedAt.informedConsentPrimaryDiseaseOrConditions)) {
                                    manuallyEdited = true;
                                }
                                englishSectionDiseaseAndConditions.push(englishDiseaseAndCondition);
                                translationsSectionDiseaseAndConditions.push(translatedDiseaseAndCondition);
                            }
                        }
                        sectionInfo.english.informedConsentPrimaryDiseaseOrConditions = englishSectionDiseaseAndConditions;
                        translationInput.informedConsentPrimaryDiseaseOrConditions = translationsSectionDiseaseAndConditions;
                    }
                    else if (propertyKey === "timelineVisits") {
                        const englishSectionTimelineVisits = [];
                        const translationsSectionTimelineVisits = [];
                        if ((englishCompositeUpdatedAt.timelineVisitPeriods &&
                            translationCompositeUpdatedAt.timelineVisitPeriods &&
                            englishCompositeUpdatedAt.timelineVisitPeriods > translationCompositeUpdatedAt.timelineVisitPeriods) ||
                            (englishCompositeUpdatedAt.timelineVisitDescriptions &&
                                translationCompositeUpdatedAt.timelineVisitDescriptions &&
                                englishCompositeUpdatedAt.timelineVisitDescriptions >
                                    translationCompositeUpdatedAt.timelineVisitDescriptions) ||
                            this.outOfDateImportedTranslations.timelineVisits) {
                            outdated = true;
                        }
                        if (translationCompositeUpdatedAt.timelineVisitPeriods &&
                            isDateAfterMostRecentTranslation(translationCompositeUpdatedAt.timelineVisitPeriods)) {
                            mostRecentTranslationUpdatedAt = translationCompositeUpdatedAt.timelineVisitPeriods;
                        }
                        if (translationCompositeUpdatedAt.timelineVisitDescriptions &&
                            isDateAfterMostRecentTranslation(translationCompositeUpdatedAt.timelineVisitDescriptions)) {
                            mostRecentTranslationUpdatedAt = translationCompositeUpdatedAt.timelineVisitDescriptions;
                        }
                        if (((translationCompositeUpdatedAt === null || translationCompositeUpdatedAt === void 0 ? void 0 : translationCompositeUpdatedAt.timelineVisitPeriods) &&
                            isManualDateAfterAIGeneratedDate(translationCompositeUpdatedAt.timelineVisitPeriods)) ||
                            ((translationCompositeUpdatedAt === null || translationCompositeUpdatedAt === void 0 ? void 0 : translationCompositeUpdatedAt.timelineVisitDescriptions) &&
                                isManualDateAfterAIGeneratedDate(translationCompositeUpdatedAt.timelineVisitDescriptions))) {
                            manuallyEdited = true;
                        }
                        for (const [index, englishTimelineVisit] of englishTimelineVisits.entries()) {
                            const translatedTimelineVisit = translationsTimelineVisits[index];
                            const translationExists = !!englishTimelineVisit.period &&
                                !!englishTimelineVisit.description &&
                                translatedTimelineVisit !== undefined &&
                                !!translatedTimelineVisit.period &&
                                !!translatedTimelineVisit.description;
                            if (!translationExists) {
                                completed = false;
                            }
                            else {
                                hasTranslations = true;
                            }
                            englishSectionTimelineVisits.push(englishTimelineVisit);
                            translationsSectionTimelineVisits.push(translatedTimelineVisit !== null && translatedTimelineVisit !== void 0 ? translatedTimelineVisit : { period: "", description: "" });
                        }
                        sectionInfo.english.timelineVisits = englishSectionTimelineVisits;
                        translationInput.timelineVisits = translationsSectionTimelineVisits;
                    }
                    else if (isEnum(ProjectSectionName)(propertyKey)) {
                        const englishSectionImages = allEnglishSectionImages.filter((image) => image.sectionName === propertyKey);
                        const translationsSectionImages = allTranslationsSectionImages.filter((image) => image.sectionName === propertyKey);
                        const englishSectionSectionImages = [];
                        const translationSectionSectionImages = [];
                        let hasCopiedImages = false;
                        for (const englishSectionImage of englishSectionImages) {
                            if (englishSectionImage.sectionName === ProjectSectionName.ADDITIONAL_INFORMATION) {
                                const englishAdditionalSection = englishAdditionalSections.find((section) => section.id === englishSectionImage.additionalSectionId);
                                if (!(englishAdditionalSection === null || englishAdditionalSection === void 0 ? void 0 : englishAdditionalSection.included)) {
                                    continue;
                                }
                            }
                            let translatedSectionImage = translationsSectionImages.find((image) => image.translatedFrom === englishSectionImage.id);
                            if (!translatedSectionImage) {
                                translatedSectionImage = {
                                    id: crypto.randomUUID(),
                                    sectionName: sectionName.startsWith("ADDITIONAL_INFORMATION")
                                        ? ProjectSectionName.ADDITIONAL_INFORMATION
                                        : sectionName,
                                    caption: "",
                                    imageUrl: "",
                                    order: (_z = englishSectionImage.order) !== null && _z !== void 0 ? _z : 0,
                                    language,
                                    status: StudySectionImageStatus.ACTIVE,
                                    translatedFrom: (_0 = englishSectionImage.id) !== null && _0 !== void 0 ? _0 : "",
                                    additionalSectionId: (_1 = englishSectionImage.additionalSectionId) !== null && _1 !== void 0 ? _1 : "",
                                    sectionType: StudySectionType.REGULAR,
                                };
                            }
                            const englishHasCaption = !!englishSectionImage.caption;
                            const translationExists = !!(translatedSectionImage === null || translatedSectionImage === void 0 ? void 0 : translatedSectionImage.caption);
                            const isCopied = translatedSectionImage.imageUrl === "" || translatedSectionImage.imageUrl === englishSectionImage.imageUrl;
                            if (isCopied) {
                                hasCopiedImages = true;
                            }
                            if (englishHasCaption && !translationExists) {
                                completed = false;
                            }
                            else if (translationExists) {
                                hasTranslations = true;
                            }
                            if ((englishSectionImage.updatedAt &&
                                (translatedSectionImage === null || translatedSectionImage === void 0 ? void 0 : translatedSectionImage.updatedAt) &&
                                englishSectionImage.updatedAt > translatedSectionImage.updatedAt) ||
                                this.outOfDateImportedTranslations[`sectionImages_${englishSectionImage.id}`]) {
                                outdated = true;
                            }
                            if ((translatedSectionImage === null || translatedSectionImage === void 0 ? void 0 : translatedSectionImage.updatedAt) && isDateAfterMostRecentTranslation(translatedSectionImage.updatedAt)) {
                                mostRecentTranslationUpdatedAt = translatedSectionImage.updatedAt;
                            }
                            if ((translatedSectionImage === null || translatedSectionImage === void 0 ? void 0 : translatedSectionImage.updatedAt) && isManualDateAfterAIGeneratedDate(translatedSectionImage.updatedAt)) {
                                manuallyEdited = true;
                            }
                            englishSectionSectionImages.push(englishSectionImage);
                            translationSectionSectionImages.push(translatedSectionImage);
                        }
                        if (hasCopiedImages) {
                            if (!copiedImagesVideosSections.includes(sectionName)) {
                                copiedImagesVideosSections.push(sectionName);
                            }
                        }
                        sectionInfo.english.sectionImages = englishSectionSectionImages;
                        translationInput.sectionImages = translationSectionSectionImages;
                    }
                    else if (typeof propertyKey === "string" && isKeyOfObject(propertyKey, englishTranslations)) {
                        if (propertyKey === "enrollmentVideo") {
                            continue;
                        }
                        if ((study.pdda && ["participantInstructionsForGuidedConsent", "hipaaAuthorizationText"].includes(propertyKey)) ||
                            ((study.pdda || !study.additionalParentGuardianEnabled) &&
                                [
                                    "additionalParentGuardianTitle",
                                    "additionalParentGuardianInstructions",
                                    "additionalParentGuardianExemptSignatureText",
                                    "additionalParentGuardianSignatureTitle",
                                    "additionalParentGuardianInformedConsentStatement",
                                ].includes(propertyKey))) {
                            continue;
                        }
                        if (englishTranslations[propertyKey]) {
                            if (!translations[propertyKey]) {
                                completed = false;
                            }
                            else {
                                hasTranslations = true;
                            }
                        }
                        if ((isKeyOfObject(propertyKey, translationCompositeUpdatedAt) &&
                            englishCompositeUpdatedAt[propertyKey] &&
                            translationCompositeUpdatedAt[propertyKey] &&
                            englishCompositeUpdatedAt[propertyKey] > translationCompositeUpdatedAt[propertyKey]) ||
                            this.outOfDateImportedTranslations[propertyKey]) {
                            outdated = true;
                        }
                        if (isKeyOfObject(propertyKey, translationCompositeUpdatedAt) &&
                            translationCompositeUpdatedAt[propertyKey] &&
                            isDateAfterMostRecentTranslation(translationCompositeUpdatedAt[propertyKey])) {
                            mostRecentTranslationUpdatedAt = translationCompositeUpdatedAt[propertyKey];
                        }
                        if (isKeyOfObject(propertyKey, translationCompositeUpdatedAt) &&
                            translationCompositeUpdatedAt[propertyKey] &&
                            isManualDateAfterAIGeneratedDate(translationCompositeUpdatedAt[propertyKey])) {
                            manuallyEdited = true;
                        }
                        ;
                        sectionInfo.english[propertyKey] = (_2 = englishTranslations[propertyKey]) !== null && _2 !== void 0 ? _2 : "";
                        if (isKeyOfObject(propertyKey, translationInputReference)) {
                            ;
                            translationInput[propertyKey] = translations[propertyKey];
                        }
                    }
                    sectionLanguageInfo[sectionName] = sectionInfo;
                    if (!completed)
                        incompleteSections.push(sectionName);
                    if (outdated)
                        outdatedSections.push(sectionName);
                    if (hasTranslations)
                        hasTranslationsSections.push(sectionName);
                    if (manuallyEdited)
                        manuallyEditedSections.push(sectionName);
                }
            }
            translationInput.additionalSections = translationSectionAdditionalSections;
        }
        const sectionLookup = availableSections.reduce((acc, section) => {
            acc[section.sectionName] = section;
            return acc;
        }, {});
        const allAvailableSections = uniqueArray(availableSections
            .filter((section) => { var _a, _b; return Object.keys((_b = (_a = sectionLanguageInfo[section.sectionName]) === null || _a === void 0 ? void 0 : _a.english) !== null && _b !== void 0 ? _b : {}).length > 0; })
            .map((section) => section.sectionName));
        const filterSectionArrayByAvailableSections = (array) => {
            return uniqueArray(array.filter((section) => allAvailableSections.includes(section)));
        };
        const finalIncompleteSections = filterSectionArrayByAvailableSections(incompleteSections);
        const result = {
            sectionLookup,
            allAvailableSections,
            incompleteSections: finalIncompleteSections,
            outdatedSections: filterSectionArrayByAvailableSections(outdatedSections),
            hasTranslationsSections: filterSectionArrayByAvailableSections(hasTranslationsSections),
            copiedSections: filterSectionArrayByAvailableSections(copiedImagesVideosSections),
            generatedWithAISections: filterSectionArrayByAvailableSections(generatedWithAISections),
            manuallyEditedSections: filterSectionArrayByAvailableSections(manuallyEditedSections),
            progress: ((allAvailableSections.length - finalIncompleteSections.length) / allAvailableSections.length) * 100,
            language,
            importType: (_4 = (_3 = study.studyTranslatableFields.find((field) => field.language === language)) === null || _3 === void 0 ? void 0 : _3.importType) !== null && _4 !== void 0 ? _4 : null,
            enabled: (_6 = (_5 = study.studyTranslatableFields.find((field) => field.language === language)) === null || _5 === void 0 ? void 0 : _5.enabled) !== null && _6 !== void 0 ? _6 : undefined,
            sectionLanguageInfo,
            translationInput,
            mostRecentTranslationUpdatedAt,
        };
        return result;
    }
    showEnglishChangesDetectedBannerIfNecessary() {
        if (!(this.hasPageBeenVisited && this.isPatientExperienceCompleted))
            return;
        this.bannerUIManager.displayBanner({
            text: "English changes detected, update your translations",
            ctaLinkText: "Go to translations",
            ctaCallback: async () => {
                await this.goToPage(ProjectStaticPageName.TRANSLATIONS);
            },
            durationInMS: 5000,
            type: "WARNING",
        });
    }
    get arePDFDownloadsReady() {
        if (this.study === NOT_LOADED || !this.study)
            return false;
        if (!this.study.previewScreenshots || this.study.previewScreenshots.length === 0)
            return false;
        return this.study.previewScreenshots.every((screenshot) => screenshot.status === ScreenshotMetadataStatus.COMPLETED);
    }
    get canUpdatePDFDownloads() {
        if (this.study === NOT_LOADED || !this.study)
            return false;
        if (!this.study.previewScreenshots || this.study.previewScreenshots.length === 0)
            return false;
        return this.study.previewScreenshots.some((screenshot) => screenshot.canUpdate === true);
    }
    showPDFDownloadReadyBannerIfAppropriate(study, oldStudy) {
        if (oldStudy === NOT_LOADED || !oldStudy)
            return;
        const diff = setDiff(this.getDownloadable(oldStudy), this.getDownloadable(study), (screenshot) => screenshot.key);
        if (diff.added.length > 0 && this.currentPage !== ProjectStaticPageName.PREVIEW) {
            this.bannerUIManager.displayBanner({
                text: "PDFs are ready for download",
                ctaLinkText: "View PDFs",
                ctaCallback: async () => {
                    await this.goToPage(ProjectStaticPageName.PREVIEW);
                },
                durationInMS: 5000,
            });
        }
    }
    getDownloadable(study) {
        var _a, _b;
        return (_b = (_a = study.previewScreenshots) === null || _a === void 0 ? void 0 : _a.filter((screenshot) => screenshot.status === ScreenshotMetadataStatus.COMPLETED)) !== null && _b !== void 0 ? _b : [];
    }
    updateSelectedTranslationFilter(filter) {
        this.selectedTranslationFilter = filter;
    }
    onStudyLoaded(studyDto, oldStudyDto, justSavedKeys = []) {
        if (!studyDto.informedConsentSectionLocked && this.informedConsentLockedDialogModel.open) {
            this.informedConsentLockedDialogModel.open = false;
        }
        void this.showMajorChangeWarningDialogIfAppropriate(studyDto, oldStudyDto, justSavedKeys);
        this.showPDFDownloadReadyBannerIfAppropriate(studyDto, oldStudyDto);
    }
    onBeforeStudySaved() {
        this.selectedLanguageTranslationsCompletedBeforeUpdate = this.selectedLanguageTranslationsCompleted;
        this.patientExperienceCompletedBeforeUpdate = this.isPatientExperienceCompleted;
    }
    onStudySaved(combinedUpdateStudyInput, studySectionImagesUpdate, additionalSectionsUpdate, icfTextSegmentsUpdate) {
        var _a, _b, _c, _d;
        if (this.study === NOT_LOADED || !this.study)
            return;
        if (this.hasTranslations && this.isPatientExperienceCompleted) {
            const updateStudyInputKeys = Object.keys(combinedUpdateStudyInput);
            const englishTranslatableFields = (_b = (_a = this.study.studyTranslatableFields) === null || _a === void 0 ? void 0 : _a.find((field) => {
                return field.language === StudyTranslationLanguage.en;
            })) !== null && _b !== void 0 ? _b : {};
            const didUpdateEnglishTranslatableField = updateStudyInputKeys.some((key) => {
                return isKeyOfObject(key, englishTranslatableFields !== null && englishTranslatableFields !== void 0 ? englishTranslatableFields : {});
            });
            const didUpdateEnglishAdditionalSection = additionalSectionsUpdate &&
                additionalSectionsUpdate.filter((section) => section.language === StudyTranslationLanguage.en).length > 0;
            const didUpdateEnglishStudySectionImagesUpdate = studySectionImagesUpdate &&
                studySectionImagesUpdate.filter((section) => section.language === StudyTranslationLanguage.en).length > 0;
            const didUpdateEnglishICFTextSegments = icfTextSegmentsUpdate && icfTextSegmentsUpdate.length > 0;
            if (didUpdateEnglishTranslatableField ||
                didUpdateEnglishAdditionalSection ||
                didUpdateEnglishStudySectionImagesUpdate ||
                didUpdateEnglishICFTextSegments) {
                this.showEnglishChangesDetectedBannerIfNecessary();
            }
        }
        if (Object.keys(this.outOfDateImportedTranslations).length > 0) {
            Object.keys((_d = (_c = combinedUpdateStudyInput.studyTranslatableFields) === null || _c === void 0 ? void 0 : _c.find((field) => field.language === this.selectedTranslationLanguage)) !== null && _d !== void 0 ? _d : {}).forEach((key) => {
                delete this.outOfDateImportedTranslations[key];
            });
            additionalSectionsUpdate === null || additionalSectionsUpdate === void 0 ? void 0 : additionalSectionsUpdate.filter((section) => section.language !== StudyTranslationLanguage.en).forEach((section) => {
                delete this.outOfDateImportedTranslations[`additionalInformation_${section.translatedFrom}`];
            });
            studySectionImagesUpdate === null || studySectionImagesUpdate === void 0 ? void 0 : studySectionImagesUpdate.filter((section) => section.language !== StudyTranslationLanguage.en).forEach((section) => {
                delete this.outOfDateImportedTranslations[`sectionImages_${section.translatedFrom}`];
            });
        }
        if (!this.selectedLanguageTranslationsCompletedBeforeUpdate && this.selectedLanguageTranslationsCompleted) {
            this.translationsManuallyCompleted = true;
        }
        if (!this.patientExperienceCompletedBeforeUpdate && this.isPatientExperienceCompleted) {
            this.shouldShowPatientExperienceCompletedDialog = true;
        }
    }
    updateShouldShowPatientExperienceCompletedDialog(show) {
        this.shouldShowPatientExperienceCompletedDialog = show;
    }
}
__decorate([
    computed
], AddProjectUIManager.prototype, "isAddProjectFlow", null);
__decorate([
    observable
], AddProjectUIManager.prototype, "isAddingAndConfirmingStudy", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "shouldShowPatientExperienceCompletedDialog", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "translationsManuallyCompleted", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "selectedTranslationFilter", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "translationsAddedThisSession", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "didDownloadTranslationsThisSession", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "outOfDateImportedTranslations", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "currentStudyHash", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "currentTab", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "informedConsentLockedDialogModel", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "hasPageBeenVisited", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "hasSectionBeenVisited", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "currentPageLoading", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "topBannerHeight", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "lastSectionOffset", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "didCloseTranslationsRequirementsChangedBanner", void 0);
__decorate([
    computed
], AddProjectUIManager.prototype, "isResearchProject", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "projectKeyParams", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "projectKey", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "projectData", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "projectDataPages", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "projectPages", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "overallProgress", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "isOverallCompleted", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "studyProgressCompleted", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "isProjectSettingsCompleted", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "isPatientExperienceCompleted", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "studyContainsUnReviewedContent", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "studyStatus", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "myStudyUser", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "studyId", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "isRelevantRoute", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "currentPage", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "currentPageIndex", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "currentProjectPageNameRoute", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "currentProjectPage", null);
__decorate([
    observable
], AddProjectUIManager.prototype, "currentProjectSectionName", void 0);
__decorate([
    computed
], AddProjectUIManager.prototype, "currentProjectSection", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "isSponsoredStudy", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "needsIdentifiedData", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "selectedLanguageTranslationsCompleted", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "allTranslationsCompleted", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "translationsCompletedLookup", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "isInterventionalStudy", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "organization", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "organizationRequire21CFRPart11ForInformedConsents", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "organizationDefaultRequire21CFRPart11ForInformedConsents", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "navSectionOffset", null);
__decorate([
    observable
], AddProjectUIManager.prototype, "isScrollingToPageSection", void 0);
__decorate([
    computed
], AddProjectUIManager.prototype, "isInformedConsentFilled", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "showStudyDetailsDialogForFirstTimeUser", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "shouldInformedConsentBeLockedByUs", null);
__decorate([
    observable
], AddProjectUIManager.prototype, "majorChangesDialogModel", void 0);
__decorate([
    computed
], AddProjectUIManager.prototype, "bottomPaddingForPage", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "detectedEnglishTranslationUpdate", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "allLanguageSectionTranslationProgressInfo", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "selectedLanguageSectionTranslationProgressInfo", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "hasTranslations", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "areTranslationsInProgress", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "hasTranslationsAndAreCompleted", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "arePDFDownloadsReady", null);
__decorate([
    computed
], AddProjectUIManager.prototype, "canUpdatePDFDownloads", null);
__decorate([
    observable
], AddProjectUIManager.prototype, "selectedLanguageTranslationsCompletedBeforeUpdate", void 0);
__decorate([
    observable
], AddProjectUIManager.prototype, "patientExperienceCompletedBeforeUpdate", void 0);
